<template>
  <div class="layout-fullscreen layout-fullscreen--no-header">
    <HeaderBar :modifiers="['main']">
      {{ title }}

      <template #right>
        <router-link
          class="button"
          :to="{ name: planId ? pageData.planPage : pageData.plansPage, params: { scope: 'plans'} }"
        >
          {{ $t('general.cancel') }}
        </router-link>
        <FormSubmitButton
          :isSaving="saving"
          @click="save"
        />
      </template>
    </HeaderBar>

    <AlertBar v-if="errorCount > 0">
      {{ pluralize(errorCount, $t('general.error'), $t('general.errors')) }} {{ $t('monitoring_plans.save_error_suffix') }}
    </AlertBar>

    <div class="layout-panel">
      <Loader
        v-if="loading"
        :modifiers="['overlay']"
      />

      <div class="layout-panel__panel">
        <template v-if="!loading">
          <div class="form">
            <div class="form__group">
              <FormRow
                :errors="errors?.plan?.name"
                :label="$t('monitoring_plans.plan_name')"
                labelFor="plan_name"
                :modifiers="['narrow']"
              >
                <div class="form-row__input">
                  <div :class="{ 'input': true, 'input--error': errors?.plan?.name }">
                    <input
                      id="plan_name"
                      v-model="plan.name"
                      type="text"
                      @change="resetValidation(errors, 'plan', 'name')"
                    >
                  </div>
                </div>
              </FormRow>

              <FormRow
                :label="$t('monitoring_plans.frequency')"
                labelFor="plan_frequency"
                :modifiers="['lateral', 'narrow']"
              >
                <div class="form-row__input">
                  <Select
                    id="plan_frequency"
                    v-model="plan.frequency"
                    :items="frequencies"
                  />
                </div>
              </FormRow>
            </div>

            <div class="form__group">
              <FormRow
                :errors="errors?.plan?.start_date"
                :label="$t('monitoring_plans.starts')"
                labelFor="plan_start_date"
                :modifiers="['lateral', 'narrow']"
              >
                <div class="form-row__input">
                  <FormDatePicker
                    id="plan_start_date"
                    v-model="plan.start_date"
                    :placeholder="$t('monitoring_plans.no_start_date')"
                    @change="resetValidation(errors, 'plan', 'start_date')"
                  />
                </div>
              </FormRow>
            </div>

            <div class="form__group">
              <FormRow
                :errors="errors?.plan?.assignee"
                :label="$t('monitoring_plans.assigned_to')"
                labelFor="plan_assignee_id"
                :modifiers="['lateral', 'narrow']"
              >
                <div class="form-row__input">
                  <Select
                    v-if="assignees"
                    id="plan_assignee_id"
                    v-model="plan.assignee_id"
                    :items="assigneeOptions"
                    :placeholder="$t('monitoring_plans.not_assigned')"
                  />
                </div>
              </FormRow>
            </div>
          </div>

          <HeaderBar :modifiers="['transparent', 'less-padding']">
            <FormRow
              :errors="errors?.plan?.points"
              :modifiers="['multi-input', 'lateral', 'error-margin-top']"
            >
              <p class="typo-ignore typo-small margin-bottom">
                {{ pluralize(plan.points.filter(notDestroyed).length, $t('activerecord.models.monitoring_plans/point')) }}
              </p>
            </FormRow>

            <template #right>
              <button
                :aria-label="$t('monitoring_plans.add_point')"
                class="button button--small"
                type="button"
                @click="addPoint"
              >
                <UseSvg
                  class="icon icon--small"
                  name="plus_16"
                />
              </button>
            </template>
          </HeaderBar>

          <EmptyMessage
            v-if="plan.points.filter(notDestroyed).length === 0"
            :message="$t('monitoring_plans.no_points_message')"
            :modifiers="['wrap-icon', 'pattern']"
            :title="$t('monitoring_plans.no_points')"
          >
            <UseSvg
              class="icon"
              name="map_pin"
            />
          </EmptyMessage>

          <div :class="{ 'scroll-container point-card-container': true, 'show-for-medium': activePoint }">
            <a
              v-for="(point, pointIndex) in plan.points.filter(notDestroyed)"
              :key="point.id"
              :class="{
                'point-card': true,
                'point-card--active': activePoint === point,
                'point-card--error': pointError(pointIndex)
              }"
              href="javascript:void(0);"
              @click="selectPoint(point)"
            >
              <div class="point-card__body">
                <div class="point-card__icon icon icon--circle"><UseSvg name="map_pin" /></div>
                <div class="point-card__header">
                  <p class="typo-ignore typo-small-title point-card__title">{{ point.name || $t('monitoring_plans.untitled_point') }}</p>

                  <div class="point-card__tasks">
                    <div class="point-card__task">
                      <div class="point-card__task-label typo-tiny">-</div>
                      <div class="point-card__task-metric typo-tiny">-</div>
                    </div>
                  </div>
                </div>
              </div>
            </a>
          </div>
        </template>
      </div>

      <div
        :class="{
          'layout-panel__panel layout-panel__panel--wide layout-panel__panel--foldable': true,
          'layout-panel__panel--shown': activePoint
        }"
      >
        <div class="layout-panel__panel-content">
          <div
            v-if="activePoint"
            class="form form--flat form--fill-vertical form--transparent"
          >
            <HeaderBar
              class="hide-for-medium"
              :modifiers="['dark', 'sticky']"
            >
              <div class="header-bar__title">
                <div class="icon icon--cirle">
                  <UseSvg
                    class="icon"
                    name="map_pin"
                  />
                </div>
                <div class="typo-title">
                  {{ $t('monitoring_plans.edit_point') }}
                </div>
              </div>

              <template #right>
                <button
                  class="button"
                  type="button"
                  @click="closePanel"
                >
                  {{ $t('general.done') }}
                </button>
              </template>
            </HeaderBar>

            <div class="form">
              <div class="form__group">
                <FormRow
                  :errors="activePointErrors?.point?.name"
                  :label="$t('monitoring_plans.point_title')"
                  :labelFor="`point_${activePoint.id}_name`"
                  :modifiers="['narrow']"
                >
                  <div class="form-row__input">
                    <div :class="{ 'input': true, 'input--error': activePointErrors?.point?.name }">
                      <input
                        :id="`point_${activePoint.id}_name`"
                        ref="pointName"
                        v-model="activePoint.name"
                        type="text"
                        @change="resetValidation(activePointErrors, 'point', 'name')"
                      >
                    </div>
                  </div>
                </FormRow>

                <FormRow
                  :errors="activePointErrors?.point?.location"
                  :label="$t('monitoring_plans.location')"
                  :labelFor="`point_${activePoint.id}_location`"
                  :modifiers="['narrow', 'no-fill']"
                >
                  <div class="form-row__input">
                    <div :class="{ 'input input--flex-grow': true, 'input--error': activePointErrors?.point?.location }">
                      <input
                        :id="`point_${activePoint.id}_location`"
                        disabled
                        type="text"
                        :value="pointLocation"
                      >
                      <div class="hide-for-medium">
                        <button
                          :aria-label="$t('monitoring_plans.open_map')"
                          class="button"
                          type="button"
                          @click="openMap"
                        >
                          <UseSvg
                            class="icon icon--small"
                            name="target"
                          />
                        </button>
                      </div>
                    </div>
                    <div class="radio-button show-for-medium">
                      <input
                        id="place_point"
                        v-model="placingPoint"
                        class="radio-button__input"
                        type="checkbox"
                      >
                      <label
                        class="radio-button__content"
                        for="place_point"
                      >
                        <span class="radio-button__label">{{ $t('monitoring_plans.place_point') }}</span>
                        <UseSvg
                          class="radio-button__icon icon icon--small"
                          name="target"
                        />
                      </label>
                    </div>
                  </div>
                </FormRow>
              </div>
            </div>

            <!-- Had to be moved out the loop -->
            <div class="hide">
              <input
                ref="fileInput"
                :aria-label="$t('monitoring_plans.attachments')"
                multiple
                type="file"
                @change="fileChange"
              >
            </div>

            <div class="form form--transparent scroll-container">
              <div class="task-card-container">
                <div
                  v-for="(task, taskIndex) in activePoint.tasks.filter(notDestroyed)"
                  :key="task.id"
                  class="task-card"
                >
                  <div class="task-card__header">
                    <div class="task-card__title typo-tiny">
                      {{ $t('monitoring_plans.task') }}
                    </div>
                    <div class="task-card__action">
                      <button
                        :aria-label="$t('monitoring_plans.delete_task')"
                        type="button"
                        @click="deleteTask(task)"
                      >
                        <UseSvg
                          class="icon icon--small"
                          name="close_circle_16"
                        />
                      </button>
                    </div>
                  </div>

                  <div class="task-card__body">
                    <div class="form form--transparent">
                      <div class="form__group">
                        <FormRow
                          :errors="taskErrors(taskIndex)?.task?.label"
                          :label="$t('monitoring_plans.label')"
                          :labelFor="`task_${task.id}_label`"
                          :modifiers="['lateral', 'narrow']"
                        >
                          <div class="form-row__input form-row__input--overflow-initial">
                            <div :class="{ 'input': true, 'input--error': taskErrors(taskIndex)?.task?.label }">
                              <input
                                :id="`task_${task.id}_label`"
                                v-model="task.label"
                                type="text"
                                @change="resetValidation(activePointErrors?.tasks[taskIndex], 'task', 'label')"
                              >
                            </div>
                          </div>
                        </FormRow>

                        <FormRow
                          :errors="taskErrors(taskIndex)?.task?.unit"
                          :label="$t('monitoring_plans.unit')"
                          :labelFor="`task_${task.id}_unit`"
                          :modifiers="['lateral', 'narrow']"
                        >
                          <div class="form-row__input form-row__input--overflow-initial">
                            <div :class="{ 'input': true, 'input--error': taskErrors(taskIndex)?.task?.unit }">
                              <input
                                :id="`task_${task.id}_unit`"
                                v-model="task.values.unit"
                                type="text"
                                @change="resetValidation(activePointErrors?.tasks[taskIndex], 'task', 'unit')"
                              >
                            </div>
                          </div>
                        </FormRow>
                        <FormRow
                          :errors="taskErrors(taskIndex)?.task?.instructions"
                          :label="$t('monitoring_plans.instructions')"
                          :labelFor="`task_${task.id}_instructions`"
                          :modifiers="['narrow']"
                        >
                          <div class="form-row__input">
                            <div class="input input--textarea">
                              <textarea
                                :id="`task_${task.id}_instructions`"
                                v-model="task.instructions"
                                rows="3"
                              />
                            </div>
                          </div>
                        </FormRow>
                      </div>
                      <div class="form__group form__group--padded">
                        <FormRow
                          :label="$t('monitoring_plans.failure_limit')"
                          :labelFor="`task_${task.id}_failure_limit`"
                          :modifiers="['lateral', 'narrow']"
                        >
                          <div class="form-row__input">
                            <button
                              :aria-label="$t('monitoring_plans.add_failure_limit')"
                              :class="{ 'button button--small': true, 'hidden': task.values.failure_condition }"
                              type="button"
                              @click="addFailureCondition(task)"
                            >
                              <UseSvg
                                class="icon icon--small"
                                name="plus_16"
                              />
                            </button>
                          </div>
                        </FormRow>

                        <!--
                        Failure Condition and Value
                        if existing, show failure condition; if not load hidden
                        -->
                        <FormRow
                          v-if="task.values.failure_condition"
                          :errors="taskErrors(taskIndex)?.task?.failure_value"
                          :modifiers="['lateral', 'light', 'multi-input', 'narrow', 'lean']"
                        >
                          <!-- Failure limit dropdown -->
                          <div class="form-row__input form-row__input--no-peek">
                            <Select
                              v-model="task.values.failure_condition"
                              :items="failureConditions"
                              :labelIcons="failureConditionIcons"
                            />
                          </div>

                          <!-- Failure value input -->
                          <div class="form-row__input form-row__input--collapse">
                            <div :class="{ 'input': true, 'input--error': taskErrors(taskIndex)?.task?.failure_value }">
                              <!-- we're using tel input type to allow keypad opening on mobile, while allowing any input characters -->
                              <input
                                :id="`task_${task.id}_failure_limit`"
                                v-model="task.values.failure_value"
                                class="text-right"
                                type="tel"
                              >
                            </div>
                          </div>

                          <!-- Remove failure condition -->
                          <div class="form-row__input">
                            <button
                              class="button button--small button--transparent"
                              type="button"
                              @click="removeFailureCondition(task)"
                            >
                              <UseSvg
                                class="icon icon--small"
                                name="close_16"
                              />
                            </button>
                          </div>
                        </FormRow>
                      </div>

                      <!-- Attachments -->
                      <div class="form__group">
                        <FormRow
                          :label="$t('monitoring_plans.attachments')"
                          :modifiers="['lateral', 'narrow']"
                        >
                          <div class="form-row__input">
                            <button
                              class="button button--small"
                              type="button"
                              @click="addAttachment(task)"
                            >
                              <UseSvg
                                class="icon icon--small"
                                name="plus_16"
                              />
                            </button>
                          </div>
                        </FormRow>

                        <div class="task-attachment-container">
                          <div
                            v-for="document in task.task_documents.filter(notDestroyed)"
                            :key="document.id"
                            :class="{ 'task-attachment': true, 'hide': !document.file_blob }"
                          >
                            <FormRow :modifiers="['lateral', 'light', 'multi-input', 'narrow', 'lean']">
                              <div class="form-row__input">
                                <div
                                  v-if="document.file_blob"
                                  class="attachment-icon icon--file"
                                >
                                  <FileIcon :contentType="document.file_blob.content_type" />
                                </div>
                              </div>

                              <div class="form-row__input form-row__input--no-peek form-row__input--expand form-row__input--wrap">
                                <div class="attachment-name typo-input">
                                  {{ document.file_blob && document.file_blob.filename }}
                                </div>
                              </div>

                              <div class="form-row__input">
                                <button
                                  class="button button--small button--transparent"
                                  type="button"
                                  @click="deleteDocument(task, document)"
                                >
                                  <UseSvg
                                    class="icon icon--small"
                                    name="close_16"
                                  />
                                </button>
                              </div>
                            </FormRow>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <FormRow :errors="activePointErrors?.point?.tasks">
                  <div :class="{ 'task-card': true, 'input--error': activePointErrors?.point?.tasks }">
                    <button
                      :aria-label="$t('monitoring_plans.add_task')"
                      class="task-card__header"
                      type="button"
                      @click="addTask"
                    >
                      <div class="task-card__title typo-tiny">
                        {{ $t('monitoring_plans.add_task') }}
                      </div>
                      <div class="task-card__action">
                        <UseSvg
                          class="icon icon--small"
                          name="plus_circle_16"
                        />
                      </div>
                    </button>
                  </div>
                </FormRow>
              </div>
            </div>

            <div class="form form--separated">
              <div class="form-row">
                <button
                  class="button button--full-width typo-ignore typo-input"
                  type="button"
                  @click="deletePoint"
                >
                  {{ $t('monitoring_plans.delete_point') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div :class="{ 'layout-panel__panel layout-panel__panel--foldable layout-panel__panel--relative': true, 'layout-panel__panel--shown': mapIsOpen }">
        <HeaderBar
          class="hide-for-medium"
          :modifiers="['dark', 'sticky']"
        >
          <div class="header-bar__title">
            <div class="icon icon--circle">
              <UseSvg name="map_pin" />
            </div>
            <div class="typo-title">
              {{ $t('monitoring_plans.set_location') }}
            </div>
          </div>
          <template #right>
            <button
              class="button"
              type="button"
              @click="closeMap"
            >
              {{ $t('general.done') }}
            </button>
          </template>
        </HeaderBar>

        <LeafletMap
          v-if="currentOperation && regions"
          ref="map"
          :class="{
            'layout-panel__panel-content': true,
            'map-container--placing-point': placingPoint
          }"
          :diagram="currentOperation?.diagram_identifier"
          :operationBounds="currentOperation?.bounds"
          :overlays="currentOperation.map_tile_overlays"
          @drag="onMapDrag"
          @init="onMapInit"
          @map-click="onMapClick"
          @marker-click="onMarkerClick"
        />

        <div
          v-if="placingPoint"
          class="place-popup place-popup--top place-popup--no-icon"
        >
          <div class="place-popup__title typo-tiny">
            {{ $t('monitoring_plans.click_to_place_point') }}
          </div>
          <button
            class="button button--transparent button--tiny place-popup__close-button"
            type="button"
            @click="placingPoint = false"
          >
            <UseSvg
              class="icon icon--small"
              name="close_circle_16"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { v4 as uuid } from 'uuid';
import { DirectUpload } from '@rails/activestorage';
import I18n from 'i18n-js';

import { DotIcon } from '@/vendor/leaflet_mods';
import strftime from '@/vendor/strftime';

import { latLngToRgeo, rgeoToLatLng, geoJSONPolygonToLatLngs, rgeoPolygonToGeoJSON } from '@/functions/geospatial_conversions';
import regionPolygonOptions from '@/functions/region_polygon_options';

import pluralize from '@/functions/pluralize';
import { screenAtLeast } from '@/functions/screen_width';
import useAjax from '@/functions/use_ajax';
import { useEquipment } from '@/functions/use_remote_object';
import { useOperation } from '@/functions/use_remote_object';

import Select from '@/components/form/select.vue';
import FormDatePicker from '@/components/form/date_picker.vue';
import FormRow from '@/components/form/row.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import AlertBar from '@/components/form/alert_bar.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import Loader from '@/components/generic/loader.vue';
import FileIcon from '@/components/generic/file_icon.vue';
import LeafletMap from '@/vendor/components/leaflet_map.vue';

const PANEL_FOLD_BREAKPOINT = 'medium';

const PointIcon = DotIcon.extend({
  options: {
    className: 'monitoring-plan-point'
  }
});

// @ts-ignore
const smallPointIcon = new PointIcon({
  iconSize: [10, 10],
  iconAnchor: [5, 5]
});

// @ts-ignore
const largePointIcon = new PointIcon({
  iconSize: [12, 12],
  iconAnchor: [6, 6]
});

const createNewPlan = () => ({
  name: '',
  points: [],
  frequency: 'weekly',
  start_date: null,
  assignee_id: null,
});

const props = defineProps({
  organisationId: { type: String, required: true },
  equipmentId: { type: String, required: false, default: null },
  operationId: { type: String, required: false, default: null },
  planId: { type: String, default: null },
});

const router = useRouter();
const ajax = useAjax();
const uploadUrl = inject('uploadUrl');

const loading = ref(true);
const currentOperation = props.equipmentId ? useEquipment(props) : useOperation(props);
const initialPlan = ref(null);
const plan = reactive(createNewPlan());
const assignees = ref(null);
const regions = ref(null);
const saving = ref(false);
const errors = ref(null);

const mapIsOpen = ref(false);
const map = ref(null);

const pointName = ref(null);
const placingPoint = ref(false);
let pointMarkers = {};

const fileInput = ref(null);
const currentTask = ref(null);

const pageData = computed(() => {
  const type = props.equipmentId ? 'equipmentData' : 'operationData';
  const data = {
    equipmentData: {
      operationType: 'equipment',
      taskType: 'Equipments::MonitoringPlans::Tasks::Measurement',
      plansPage: 'equipmentPlans',
      planPage: 'equipmentPlan',
      plansPath: `/organisations/${props.organisationId}/equipment/${props.equipmentId}/monitoring_plans/plans`,
      planPath: `/organisations/${props.organisationId}/equipment/${props.equipmentId}/monitoring_plans/plans/${props.planId}`,
      assigneesPath: `/organisations/${props.organisationId}/equipment/${props.equipmentId}/inspectors`,
      regionsPath: `/organisations/${props.organisationId}/equipment/${props.equipmentId}/regions`,
    },
    operationData: {
      operationType: 'operations',
      taskType: 'MonitoringPlans::Tasks::Measurement',
      plansPage: 'plans',
      planPage: 'plan',
      plansPath: `/organisations/${props.organisationId}/operations/${props.operationId}/monitoring_plans/plans`,
      planPath: `/organisations/${props.organisationId}/operations/${props.operationId}/monitoring_plans/plans/${props.planId}`,
      assigneesPath: `/organisations/${props.organisationId}/operations/${props.operationId}/inspectors`,
      regionsPath: `/organisations/${props.organisationId}/operations/${props.operationId}/regions`,
    },
  };
  return data[type];
});

const assigneeOptions = computed(() => assignees.value?.map(assignee => ({
  label: assignee.full_name,
  value: assignee.id,
})) || []);

const frequencies = [
  { value: 'daily', label: I18n.t('frequencies.daily') },
  { value: 'weekly', label: I18n.t('frequencies.weekly') },
  { value: 'monthly', label: I18n.t('frequencies.monthly') },
  { value: 'annual', label: I18n.t('frequencies.annual') },
];

const failureConditions = [
  { value: 'less_than', label: I18n.t('failure_conditions.less_than') },
  { value: 'greater_than', label: I18n.t('failure_conditions.greater_than') },
];

const failureConditionIcons = {
  less_than: { name: 'below', class: 'icon icon--error' },
  greater_than: { name: 'above', class: 'icon icon--error' },
};

const title = props.planId ? I18n.t('monitoring_plans.edit_plan') : I18n.t('monitoring_plans.new_plan');

const planPath = computed(() => {
  if (props.planId) {
    return `${pageData.value.plansPath}/${props.planId}`;
  }
  return pageData.value.plansPath;
});

const activePoint = ref(null);
const activePointIndex = computed(() => {
  return plan.points.indexOf(activePoint.value);
});

const activePointErrors = computed(() => {
  if (!activePoint.value) return null;
  if (!errors.value) return null;
  return errors.value.points[activePointIndex.value];
});

const taskErrors = (index) => {
  if (!activePointErrors.value) return null;
  return activePointErrors.value.tasks[index];
};

const pointLocation = computed(() => {
  if (activePoint.value && activePoint.value.location) {
    let [lat, lng] = rgeoToLatLng(activePoint.value.location);
    resetValidation(activePointErrors.value, 'point', 'location');
    resetValidation(errors.value, 'plan', 'points.location');
    return `${lng}, ${lat}`;
  }
  return null;
});

const errorCount = computed(() => {
  let countPlanErrors = 0;
  let countPointErrors = 0;
  if (errors.value?.plan) {
    Object.keys(errors.value?.plan).forEach(plan => {
      if (!plan.includes('points')) countPlanErrors = countPlanErrors + 1;
    });
  }

  errors.value?.points?.forEach((_, index) => {
    countPointErrors = pointError(index);
  });
  return countPlanErrors + countPointErrors;
});

onMounted(async () => {
  loading.value = true;
  await Promise.all([
    loadInitialPlan(),
    loadAssignees(),
    loadRegions()
  ]);
  loading.value = false;
});

onBeforeUnmount(() => {
  window.removeEventListener('eaimms:breakpoint.change', testWidth);
  document.body.removeEventListener('keydown', escapeHandler);
});

watch(activePoint, () => {
  plan.points.forEach(point => {
    if (pointMarkers[point.id]) {
      pointMarkers[point.id].setIcon(point === activePoint.value ? largePointIcon : smallPointIcon);
    }
  });

  if (activePoint.value && activePoint.value.location) {
    map.value?.setCenter(rgeoToLatLng(activePoint.value.location));
  }
});

watch(plan, () => {
  if (plan.assignee_id) resetValidation(errors.value, 'plan', 'assignee');
});

watch(currentTask, (value, oldValue) => {
  if (value?.id !== oldValue?.id) return fileInput.value.value = '';
});

function pointError(index) {
  let countPointErrors = 0;
  let countTaskErrors = 0;
  if (errors.value?.points[index]?.point) {
    Object.keys(errors.value?.points[index]?.point).forEach(point_plan => {
      if (!point_plan.includes('tasks')) countPointErrors = countPointErrors + 1;
    });

    errors.value?.points[index]?.tasks?.forEach(task => {
      if (task?.task) {
        countTaskErrors = countTaskErrors + Object.keys(task?.task)?.length;
      }
    });
  }
  return countPointErrors + countTaskErrors;
}

async function loadInitialPlan() {
  if (props.planId) {
    let { ok, data } = await ajax(pageData.value.planPath);

    if (ok) {
      if (data.start_date) {
        data.start_date = new Date(data.start_date);
      }

      initialPlan.value = data;
      Object.assign(plan, initialPlan.value);
      if (initialPlan.value.points.length > 0 && screenAtLeast('medium')) {
        selectPoint(initialPlan.value.points[0]);
      }
    }
  } else {
    initialPlan.value = createNewPlan();
  }
}

async function loadAssignees() {
  let { ok, data } = await ajax(
    pageData.value.assigneesPath,
    { query: { assignable: true } }
  );

  if (ok) {
    assignees.value = data;
  }
}

async function loadRegions() {
  let { ok, data } = await ajax(pageData.value.regionsPath);

  if (ok) {
    regions.value = data.regions;
  }
}

function loadRegion(region) {
  let geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  let latLngs = geoJSONPolygonToLatLngs(geoJSON);
  let polygon = map.value.addPolygon(latLngs, regionPolygonOptions(region.region_type));
  let regionObject = {
    ...region,
    geoJSON,
    latLngs,
    polygon,
  };
  polygon.region = regionObject;
}

function isoDate(date) {
  if (!date) return null;

  return strftime('%Y-%m-%d', date);
}

async function formatTaskDocumentForPost(doc) {
  if (doc.isNew) {
    await uploadDocument(doc);
    return { file: doc.file_blob.signed_id };
  } else {
    return { id: doc.id, _destroy: doc._destroy };
  }
}

async function formatTaskForPost(task) {
  let t = {
    label: task.label,
    values: task.values,
    instructions: task.instructions,
    type: pageData.value.taskType,
    task_documents_attributes: await Promise.all(task.task_documents?.map(formatTaskDocumentForPost) || []),
  };

  if (!task.isNew) {
    if (task._destroy) {
      return { id: task.id, _destroy: true };
    }
    t.id = task.id;
  }

  return t;
}

async function formatPointForPost(point) {
  let p = {
    name: point.name,
    location: point.location,
    tasks_attributes: await Promise.all(point.tasks?.map(formatTaskForPost) || []),
  };

  if (!point.isNew) {
    if (point._destroy) {
      return { id: point.id, _destroy: true };
    }
    p.id = point.id;
  }

  return p;
}

async function formatPlanForPost() {
  return {
    name: plan.name,
    frequency: plan.frequency,
    start_date: isoDate(plan.start_date),
    assignee_id: plan.assignee_id,
    points_attributes: await Promise.all(plan.points?.map(formatPointForPost) || []),
  };
}

async function save() {
  saving.value = true;
  let url = planPath.value;
  let method = props.planId ? 'PUT' : 'POST';
  let { status, data } = await ajax(url, { method, body: { plan: await formatPlanForPost() } });

  if (status === 422) {
    errors.value = data;
  } else {
    router.push({ name: pageData.value.planPage, params: { scope: 'plans', planId: data.id } });
  }

  saving.value = false;
}

async function selectPoint(point) {
  activePoint.value = point;

  await nextTick();
  pointName.value.focus();
}

function addPoint() {
  let point = {
    isNew: true,
    id: uuid(),
    tasks: [],
  };
  plan.points.push(point);
  selectPoint(point);
  addTask();
  resetValidation(errors.value, 'plan', 'points');
}

function closePanel() {
  activePoint.value = null;
}

function onMapInit() {
  if (regions.value) {
    regions.value.forEach(loadRegion);
  }

  plan.points.forEach(point => {
    addMapMarker(point, rgeoToLatLng(point.location), smallPointIcon);
  });

  if (plan.points.length > 0 && screenAtLeast(PANEL_FOLD_BREAKPOINT)) {
    selectPoint(plan.points[0]);
  }

  window.addEventListener('eaimms:breakpoint.change', testWidth, { passive: true });
  testWidth();

  document.body.addEventListener('keydown', escapeHandler, false);
}

function escapeHandler(event) {
  if(event.key === 'Escape' && placingPoint.value) {
    placingPoint.value = false;
    event.preventDefault();
  }
}

function onMapDrag([lat, lng]) {
  if (mapIsOpen.value && activePoint.value) {
    setActivePointLocation(lat, lng);
  }
}

function testWidth() {
  if (screenAtLeast(PANEL_FOLD_BREAKPOINT)) {
    closeMap();
  }
}

function openMap() {
  if (!screenAtLeast(PANEL_FOLD_BREAKPOINT)) {
    mapIsOpen.value = true;
    nextTick(async () => {
      map.value.enableCrosshair();
      await map.value.refresh();
      if (activePoint.value) {
        let point = pointMarkers[activePoint.value.id];
        if (point) map.value.goTo(point.getLatLng(), 15);
      }
    });
  }
}

function closeMap() {
  mapIsOpen.value = false;
  map.value.disableCrosshair();
}

function deleteTask(task) {
  if (task.isNew) {
    let i = activePoint.value.tasks.indexOf(task);
    if (i !== -1) {
      activePoint.value.tasks.splice(i, 1);
    }
  } else {
    task._destroy = true;
  }
}

function addTask() {
  activePoint.value.tasks.push({
    isNew: true,
    id: uuid(),
    values: {},
    task_documents: [],
  });

  resetValidation(activePointErrors.value, 'point', 'tasks');
}

function resetValidation(obj, parent, target) {
  if (!obj || !obj[parent]) return;
  /**
   * Removes the target key and it's data
   * to reset the validation when adding more tasks
   */
  const { [target]:ignore, ...filteredObject } = obj[parent];
  obj[parent] = filteredObject;
}

function deletePoint() {
  if (activePoint.value.isNew) {
    let i = plan.points.indexOf(activePoint.value);
    if (i !== -1) {
      plan.points.splice(i, 1);
    }
  } else {
    activePoint.value._destroy = true;
  }
  let marker = pointMarkers[activePoint.value.id];
  if (marker) {
    map.value.removeObject(marker);
    delete pointMarkers[activePoint.value.id];
  }
  activePoint.value = null;
}

function addMapMarker(point, latLng, icon) {
  let marker = map.value.addMarker(latLng, { icon });
  marker.planPoint = point;
  pointMarkers[point.id] = marker;
}

function onMarkerClick(marker) {
  if (!placingPoint.value) {
    selectPoint(marker.planPoint);
  }
}

function setActivePointLocation(lat, lng) {
  activePoint.value.location = latLngToRgeo([lat, lng]);
  let marker = pointMarkers[activePoint.value.id];
  if (marker) {
    marker.setLatLng([lat, lng]);
  } else {
    addMapMarker(activePoint.value, [lat, lng], largePointIcon);
  }
}

function onMapClick({ latlng: { lat, lng } }) {
  if (!placingPoint.value) return;

  setActivePointLocation(lat, lng);
  placingPoint.value = false;
}

function addFailureCondition(task) {
  task.values.failure_condition = 'less_than';
}

function removeFailureCondition(task) {
  task.values.failure_condition = null;
  task.values.failure_value = null;
}

async function addAttachment(task) {
  currentTask.value = task;
  await nextTick();
  fileInput.value.focus();
  fileInput.value.click();
}

function fileChange(event) {
  const { files } = event.target;
  Object.values(files).forEach((file) => {
    const document = {
      isNew: true,
      id: uuid(),
      upload: new DirectUpload(file, uploadUrl),
      file_blob: { filename: file.name, content_type: file.type },
    };
    currentTask.value.task_documents.push(document);
  });
}

function uploadDocument(document) {
  return new Promise((resolve, reject) => {
    document.upload.create((error, blob) => {
      if (error) {
        reject(error);
        return;
      }
      document.file_blob = blob;
      resolve(blob);
    });
  });
}

function deleteDocument(task, document) {
  if (document.isNew) {
    let i = task.task_documents.indexOf(document);
    if (i !== -1) {
      task.task_documents.splice(i, 1);
    }
  } else {
    document._destroy = true;
  }
}

function notDestroyed(x){
  return !x._destroy;
}
</script>
